import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "../../api/axios";

// Auth rides the axios interceptor (auth.access_token), same as every other page
// in this panel — nothing to pass by hand.
const IB = "/api/admin/ib";

const root = ["forex-ib"];

// The shared ErrorBanner only prints a useful heading for an ApiError; this
// instance throws a raw AxiosError, so a failed query would read "Request failed
// with status code 400". Re-throw with the backend's own message instead.
const unwrap = (p) =>
  p.catch((e) => {
    throw new Error(e?.response?.data?.message || e.message);
  });

// No pagination on these routes: they answer a plain array, not the
// { data, currentPage, totalPages } envelope the rest of the panel uses.
// Blank filters are dropped — `?status=bogus` is not rejected, it silently
// returns an empty list, so only ever send values from the option lists below.
const fetchRequests = (f) =>
  unwrap(
    axiosInstance
      .get(`${IB}/requests`, {
        params: {
          ...(f.status ? { status: f.status } : {}),
          ...(f.type ? { type: f.type } : {}),
        },
      })
      .then((r) => r.data.data),
  );

export function useIbRequests(filters) {
  return useQuery({
    queryKey: [...root, "requests", filters],
    queryFn: () => fetchRequests(filters),
    refetchOnWindowFocus: true, // live work list
    staleTime: 0,
  });
}

// `total` sits at the TOP level of the envelope, beside `data`, not inside it.
const fetchBanked = () =>
  unwrap(
    axiosInstance.get(`${IB}/banked`).then((r) => ({
      rows: r.data.data,
      total: r.data.total,
    })),
  );

export function useBankedIncome() {
  return useQuery({
    queryKey: [...root, "banked"],
    queryFn: fetchBanked,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}

// onSettled, not onSuccess: if another admin decided this row first the call
// fails, and the stale list still has to be dropped and refetched.
const useDecision = (mutationFn, key) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSettled: () => queryClient.invalidateQueries({ queryKey: [...root, key] }),
  });
};

// `note` is optional and comes back on the decided row as `reviewNote`.
// Approving a levels request re-checks the conditions and can answer
// "Conditions are no longer met" with the current figures in `data` — a normal
// outcome, not a failure of the call.
export const useApproveIbRequest = () =>
  useDecision(
    ({ requestId, note }) =>
      axiosInstance.post(`${IB}/requests/approve`, { requestId, note }),
    "requests",
  );

export const useRejectIbRequest = () =>
  useDecision(
    ({ requestId, note }) =>
      axiosInstance.post(`${IB}/requests/reject`, { requestId, note }),
    "requests",
  );

// Immediate, not queued: this pays straight into the wallet and the release job
// never picks these up. There is no undo.
export const useReleaseBanked = () =>
  useDecision(
    (incomeId) => axiosInstance.post(`${IB}/banked/release`, { incomeId }),
    "banked",
  );

// The backend does not validate these — an unrecognised value returns 200 with
// an empty list rather than a 400, so never let an admin type one.
export const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

export const TYPE_OPTIONS = [
  { value: "", label: "All types" },
  { value: "activate", label: "Activate" },
  { value: "levels", label: "Levels 2 & 3" },
];

// The four conditions for opening levels 2 and 3, measured from registration.
// Only ever populated on a `levels` request — an `activate` row's snapshot is null.
export const SNAPSHOT_FIELDS = [
  { key: "margin", label: "Margin", target: 300, money: true },
  { key: "ownLots", label: "Own lots", target: 5 },
  { key: "activeDirects", label: "Active directs", target: 5 },
  { key: "directLots", label: "Direct lots", target: 40 },
];
